import React, { useMemo } from 'react';
import { formatDateEU } from '../../utils/dateFormat';
import { getTaskDateRange, isDoneLike, isOverdue } from '../../utils/myWorkTaskUtils';

function parseYmdLocal(ymd) {
  const s = String(ymd || '').slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

function toYmdLocal(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function startOfWeekMondayLocal(d) {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = date.getDay(); // 0=Sun..6=Sat
  const offset = dow === 0 ? -6 : 1 - dow;
  date.setDate(date.getDate() + offset);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function endOfWeekSundayLocal(d) {
  const monday = startOfWeekMondayLocal(d);
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate());
  sunday.setDate(sunday.getDate() + 6);
  return sunday;
}

function addDaysLocal(d, days) {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  date.setDate(date.getDate() + Number(days || 0));
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function taskLabel(a) {
  const projectName = (a?.project && a.project.name) || a?.project_name || (a?.project_id ? `Project #${a.project_id}` : 'Project');
  const jobName = a?.job?.name || a?.job_name || (a?.job_id ? `Job #${a.job_id}` : 'Job');
  return `${jobName} — ${projectName}`;
}

export default function MyWorkCalendarView({
  rangeStart,
  rangeEnd,
  tasksByDay,
  todayYmd,
  isAurora,
  theme,
  onSelectTask,
}) {
  const grid = useMemo(() => {
    const start = parseYmdLocal(rangeStart);
    const end = parseYmdLocal(rangeEnd);
    if (!start || !end) return null;

    const gridStart = startOfWeekMondayLocal(start);
    const gridEnd = endOfWeekSundayLocal(end);

    const days = [];
    for (let d = new Date(gridStart); d <= gridEnd; d = addDaysLocal(d, 1)) {
      const ymd = toYmdLocal(d);
      days.push({
        ymd,
        date: new Date(d.getFullYear(), d.getMonth(), d.getDate()),
        inRange: ymd >= String(rangeStart) && ymd <= String(rangeEnd),
      });
      if (days.length > 370) break; // safety
    }

    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      const w = days.slice(i, i + 7);
      if (w.length === 7) weeks.push(w);
    }

    return { gridStart, gridEnd, days, weeks };
  }, [rangeStart, rangeEnd]);

  const events = useMemo(() => {
    const byId = new Map(); // assignmentId -> { a, openYmd }

    if (tasksByDay && typeof tasksByDay.entries === 'function') {
      for (const [ymd, list] of tasksByDay.entries()) {
        for (const a of (Array.isArray(list) ? list : [])) {
          const aid = String(a?.assignment_id || a?.id || '').trim();
          if (!aid) continue;
          const prev = byId.get(aid);
          if (!prev) {
            byId.set(aid, { a, openYmd: String(ymd) });
          } else {
            if (String(ymd) < String(prev.openYmd)) byId.set(aid, { a: prev.a || a, openYmd: String(ymd) });
          }
        }
      }
    }

    const out = [];
    for (const [aid, row] of byId.entries()) {
      const a = row?.a;
      const r = getTaskDateRange(a);
      const start = r?.start || '';
      const end = r?.end || '';
      if (!start && !end) continue; // undated: keep calendar read-focused
      const done = isDoneLike(a);
      const overdue = isOverdue(a, { todayYmd });

      out.push({
        id: aid,
        a,
        label: taskLabel(a),
        startYmd: start,
        endYmd: end,
        openYmd: row?.openYmd || start || end,
        done,
        overdue,
      });
    }

    out.sort((x, y) => {
      const a = String(x.startYmd || '');
      const b = String(y.startYmd || '');
      if (a !== b) return a.localeCompare(b);
      return String(x.endYmd || '').localeCompare(String(y.endYmd || ''));
    });
    return out;
  }, [tasksByDay, todayYmd]);

  if (!grid) {
    return (
      <div className="text-sm text-gray-600 dark:text-gray-300">No range selected.</div>
    );
  }

  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const MAX_LANES = 4;

  return (
    <div
      className="rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
      style={isAurora ? { background: theme.surface2 || theme.surface, borderColor: theme.border } : undefined}
    >
      <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700" style={isAurora ? { borderColor: theme.border } : undefined}>
        {weekdays.map((w) => (
          <div key={w} className="px-2 py-2 text-xs font-semibold text-gray-700 dark:text-gray-200" style={isAurora ? { color: theme.text } : undefined}>
            {w}
          </div>
        ))}
      </div>

      <div>
        {(Array.isArray(grid.weeks) ? grid.weeks : []).map((week, idx) => {
          const weekStartYmd = week[0]?.ymd;
          const weekEndYmd = week[6]?.ymd;
          const indexByYmd = new Map(week.map((c, i) => [c.ymd, i]));

          const segments = [];
          for (const ev of (Array.isArray(events) ? events : [])) {
            if (!ev?.startYmd || !ev?.endYmd) continue;
            if (String(ev.endYmd) < String(weekStartYmd)) continue;
            if (String(ev.startYmd) > String(weekEndYmd)) continue;
            const segStart = String(ev.startYmd) < String(weekStartYmd) ? String(weekStartYmd) : String(ev.startYmd);
            const segEnd = String(ev.endYmd) > String(weekEndYmd) ? String(weekEndYmd) : String(ev.endYmd);
            const startIdx = indexByYmd.get(segStart);
            const endIdx = indexByYmd.get(segEnd);
            if (startIdx == null || endIdx == null) continue;
            segments.push({ ev, startIdx, endIdx, segStart, segEnd });
          }

          segments.sort((a, b) => {
            if (a.startIdx !== b.startIdx) return a.startIdx - b.startIdx;
            return (b.endIdx - b.startIdx) - (a.endIdx - a.startIdx);
          });

          const lanes = []; // number[] lastEndIdx
          const laneSegs = []; // Array<Array<seg>>
          for (const seg of segments) {
            let laneIndex = -1;
            for (let i = 0; i < lanes.length; i++) {
              if (seg.startIdx > lanes[i]) { laneIndex = i; break; }
            }
            if (laneIndex < 0) {
              laneIndex = lanes.length;
              lanes.push(-1);
              laneSegs.push([]);
            }
            lanes[laneIndex] = seg.endIdx;
            laneSegs[laneIndex].push(seg);
          }

          const renderLaneCount = Math.min(MAX_LANES, laneSegs.length);
          const hiddenCount = Math.max(0, laneSegs.length - renderLaneCount);

          return (
            <div key={`${weekStartYmd || idx}`}
                 className="border-b border-gray-200 dark:border-gray-700"
                 style={isAurora ? { borderColor: theme.border } : undefined}
            >
              <div className="grid grid-cols-7">
                {week.map((cell) => {
                  const isToday = !!todayYmd && String(todayYmd) === String(cell.ymd);
                  return (
                    <div
                      key={cell.ymd}
                      className={
                        'p-2 border-r border-gray-200 dark:border-gray-700 '
                        + (cell.inRange ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900/20')
                      }
                      style={isAurora ? { background: cell.inRange ? theme.surface : theme.surface2, borderColor: theme.border } : undefined}
                    >
                      <div className="flex items-center justify-between">
                        <div
                          className={
                            'text-xs font-semibold '
                            + (cell.inRange ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500')
                          }
                          style={isAurora ? { color: cell.inRange ? theme.text : theme.mutedText } : undefined}
                          title={formatDateEU(cell.date) || cell.ymd}
                        >
                          {String(cell.date.getDate())}
                        </div>
                        {isToday && (
                          <div
                            className="text-[10px] px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-700"
                            style={isAurora ? { borderColor: theme.border, color: theme.text, background: theme.selected || 'transparent' } : undefined}
                          >
                            Today
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="px-2 pb-2">
                {Array.from({ length: renderLaneCount }).map((_, laneIdx) => (
                  <div key={`${weekStartYmd}-lane-${laneIdx}`} className="grid grid-cols-7 gap-1 mt-1">
                    {laneSegs[laneIdx].map((seg) => {
                      const ev = seg.ev;
                      const rangeLabel = (() => {
                        const s = ev?.startYmd ? formatDateEU(new Date(String(ev.startYmd) + 'T00:00:00')) : '';
                        const e = ev?.endYmd ? formatDateEU(new Date(String(ev.endYmd) + 'T00:00:00')) : '';
                        return (s && e) ? `${s} – ${e}` : (s || e || '');
                      })();

                      const className = (() => {
                        if (ev?.overdue) {
                          return 'rounded border px-2 py-1 text-xs font-semibold text-red-800 bg-red-50 border-red-300 dark:bg-red-900/20 dark:text-red-200 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/30';
                        }
                        if (ev?.done) {
                          return 'rounded border px-2 py-1 text-xs font-medium text-green-800 bg-green-50 border-green-200 dark:bg-green-900/20 dark:text-green-200 dark:border-green-800/60 hover:bg-green-100 dark:hover:bg-green-900/30';
                        }
                        return 'rounded border px-2 py-1 text-xs text-gray-800 bg-white border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30';
                      })();

                      const style = isAurora
                        ? {
                            background: ev?.overdue ? (theme.surface2 || theme.surface) : theme.surface,
                            borderColor: ev?.overdue ? (theme.danger || theme.border) : theme.border,
                            color: theme.text,
                          }
                        : undefined;

                      return (
                        <button
                          key={`${weekStartYmd}-${laneIdx}-${ev.id}-${seg.segStart}`}
                          type="button"
                          className={className}
                          style={{ ...(style || {}), gridColumn: `${seg.startIdx + 1} / ${seg.endIdx + 2}` }}
                          title={rangeLabel ? `${ev.label} • ${rangeLabel}` : ev.label}
                          onClick={() => {
                            if (typeof onSelectTask === 'function') onSelectTask(ev.a, ev.openYmd);
                          }}
                        >
                          <div className="truncate">
                            {ev.label}
                            {ev?.overdue ? <span className="ml-2">Overdue</span> : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}

                {hiddenCount > 0 && (
                  <div className="mt-2 text-xs text-gray-600 dark:text-gray-300" style={isAurora ? { color: theme.mutedText } : undefined}>
                    +{hiddenCount} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
